import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { useEffect, useMemo } from 'react';
import { TOTAL_STEPS_FROM_PORTAL, useProjectFormStore } from '@/lib/project-form-store';
import { trpc } from '@/lib/trpc';
import { ProjectNewProvider } from './-components/project-new-context';
import { ProjectNewLayout } from './-components/project-new-layout';

export const Route = createFileRoute('/ngos/portals/$portalId_/projects/new')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useLingui();
	const { portalId } = Route.useParams();
	const navigate = useNavigate();
	const { data: portal } = useQuery(trpc.ngo.getPortal.queryOptions({ portalId }));
	const portalTitle = portal && 'title' in portal ? portal.title : undefined;

	useEffect(() => {
		// Keep the store reset in the lazy component chunk so the route wrapper stays preload-light.
		return () => {
			useProjectFormStore.getState().reset();
		};
	}, []);

	const navigation = useMemo(
		() => ({
			toIndex: () => navigate({ to: '/ngos/portals/$portalId/projects/new', params: { portalId } }),
			toBasic: () => navigate({ to: '/ngos/portals/$portalId/projects/new/basic', params: { portalId } }),
			toDetails: () => navigate({ to: '/ngos/portals/$portalId/projects/new/details', params: { portalId } }),
			toFundingType: () =>
				navigate({ to: '/ngos/portals/$portalId/projects/new/funding-details', params: { portalId } }),
			toFundingDetails: () =>
				navigate({ to: '/ngos/portals/$portalId/projects/new/funding-details', params: { portalId } }),
			toSuccess: () => navigate({ to: '/ngos/portals/$portalId/projects/new/success', params: { portalId } }),
			toCancel: () => navigate({ to: '/ngos/portals/$portalId', params: { portalId } }),
		}),
		[navigate, portalId],
	);

	return (
		<ProjectNewProvider
			value={{
				fromPortal: true,
				portalId,
				totalSteps: TOTAL_STEPS_FROM_PORTAL,
				navigation,
				breadcrumbs: [
					{ label: t`My Portals`, onClick: () => navigate({ to: '/ngos/portals' }) },
					{ label: portalTitle ?? portalId, onClick: navigation.toCancel },
				],
			}}
		>
			<ProjectNewLayout />
		</ProjectNewProvider>
	);
}
